/*───────────────────────────────────────────────────────────────
 📄 File: sidebarIcons.tsx
 🧩 Description:
    Icon resolver for Sidebar.tsx.
    Maps icon name strings (from the DB menu) to Lucide React elements.
────────────────────────────────────────────────────────────────*/

import React from 'react';
import {
    Settings, User, Bell, Search, BarChart3, Phone, Shield, Users,
    FileText, Layers, List, UserPlus, Key, LogOut, Headphones, Route,
    Building, Wrench, Plus, AlertCircle, Download, MessageCircle, Bot,
    Cpu, Volume2, Wifi, Link, Puzzle, Palette, Server, Mail, Lock, Globe,
    Clock, Calendar, Moon, Edit, Video, Code, MapPin, Folder, Hash,
    Megaphone, Hand, Car, Zap, ExternalLink, Navigation, Tag, Ban,
    CreditCard, ArrowUp, Crown, Cable, ArrowRight, ArrowLeft, RefreshCw,
    Bed, Coffee, Terminal, Activity, CheckSquare, Filter, BarChart2,
    Smartphone, Music, Mic, Printer, Send, Eye, Book, AlertTriangle,
    Edit3, Factory, Briefcase, AppWindow, Grid, MessageSquare, Languages
} from 'lucide-react';

interface IconProps {
    className?: string;
    size?: number;
}

export function getIconByName(iconName: string, iconProps: IconProps): React.ReactElement {
    switch (iconName) {
        case 'phone':
        case 'phone-call':
        case 'phone-incoming':
            return <Phone {...iconProps} />;
        case 'file-text':
        case 'file-template':
            return <FileText {...iconProps} />;
        case 'settings': return <Settings {...iconProps} />;
        case 'user':
        case 'user-cog':
        case 'circle-user':
            return <User {...iconProps} />;
        case 'layers': return <Layers {...iconProps} />;
        case 'grid-3x3':
        case 'grid-2x2':
        case 'layout-grid':
        case 'grid':
            return <Grid {...iconProps} />;
        case 'app-window':
            return <AppWindow {...iconProps} />;
        case 'users': return <Users {...iconProps} />;
        case 'shield':
        case 'certificate':
            return <Shield {...iconProps} />;
        case 'list': return <List {...iconProps} />;
        case 'user-plus': return <UserPlus {...iconProps} />;
        case 'key': return <Key {...iconProps} />;
        case 'bar-chart-3': return <BarChart3 {...iconProps} />;
        case 'headphones': return <Headphones {...iconProps} />;
        case 'route': return <Route {...iconProps} />;
        case 'building':
        case 'building-2':
            return <Building {...iconProps} />;
        case 'wrench': return <Wrench {...iconProps} />;
        case 'plus': return <Plus {...iconProps} />;
        case 'alert-circle': return <AlertCircle {...iconProps} />;
        case 'download': return <Download {...iconProps} />;
        case 'message-circle': return <MessageCircle {...iconProps} />;
        case 'message-square': return <MessageSquare {...iconProps} />;
        case 'bot': return <Bot {...iconProps} />;
        case 'cpu': return <Cpu {...iconProps} />;
        case 'voicemail':
        case 'mic':
            return <Mic {...iconProps} />;
        case 'volume-2': return <Volume2 {...iconProps} />;
        case 'wifi': return <Wifi {...iconProps} />;
        case 'link': return <Link {...iconProps} />;
        case 'puzzle': return <Puzzle {...iconProps} />;
        case 'palette': return <Palette {...iconProps} />;
        case 'server': return <Server {...iconProps} />;
        case 'mail': return <Mail {...iconProps} />;
        case 'lock': return <Lock {...iconProps} />;
        case 'globe': return <Globe {...iconProps} />;
        case 'clock': return <Clock {...iconProps} />;
        case 'calendar': return <Calendar {...iconProps} />;
        case 'moon': return <Moon {...iconProps} />;
        case 'edit': return <Edit {...iconProps} />;
        case 'search': return <Search {...iconProps} />;
        case 'video': return <Video {...iconProps} />;
        case 'code': return <Code {...iconProps} />;
        case 'map-pin': return <MapPin {...iconProps} />;
        case 'folder': return <Folder {...iconProps} />;
        case 'hash': return <Hash {...iconProps} />;
        case 'megaphone': return <Megaphone {...iconProps} />;
        case 'hand': return <Hand {...iconProps} />;
        case 'car': return <Car {...iconProps} />;
        case 'zap': return <Zap {...iconProps} />;
        case 'external-link': return <ExternalLink {...iconProps} />;
        case 'navigation': return <Navigation {...iconProps} />;
        case 'tag': return <Tag {...iconProps} />;
        case 'ban': return <Ban {...iconProps} />;
        case 'credit-card': return <CreditCard {...iconProps} />;
        case 'arrow-up': return <ArrowUp {...iconProps} />;
        case 'crown': return <Crown {...iconProps} />;
        case 'cable': return <Cable {...iconProps} />;
        case 'arrow-right': return <ArrowRight {...iconProps} />;
        case 'arrow-left': return <ArrowLeft {...iconProps} />;
        case 'refresh-cw': return <RefreshCw {...iconProps} />;
        case 'bed': return <Bed {...iconProps} />;
        case 'coffee': return <Coffee {...iconProps} />;
        case 'terminal': return <Terminal {...iconProps} />;
        case 'activity':
        case 'monitor':
            return <Activity {...iconProps} />;
        case 'check-square': return <CheckSquare {...iconProps} />;
        case 'filter': return <Filter {...iconProps} />;
        case 'bar-chart-2': return <BarChart2 {...iconProps} />;
        case 'smartphone': return <Smartphone {...iconProps} />;
        case 'music': return <Music {...iconProps} />;
        case 'printer': return <Printer {...iconProps} />;
        case 'send': return <Send {...iconProps} />;
        case 'eye': return <Eye {...iconProps} />;
        case 'book': return <Book {...iconProps} />;
        case 'alert-triangle': return <AlertTriangle {...iconProps} />;
        case 'edit-3': return <Edit3 {...iconProps} />;
        case 'factory': return <Factory {...iconProps} />;
        case 'briefcase': return <Briefcase {...iconProps} />;
        case 'bell': return <Bell {...iconProps} />;
        case 'log-out': return <LogOut {...iconProps} />;
        case 'languages': return <Languages {...iconProps} />;
        default:
            return <Phone {...iconProps} />;
    }
}
